import { ActionEvents } from '@griply/types';
import { useUpdateGoal } from 'features/goals';
import React, { useCallback, useMemo, useState } from 'react';
import { GoalProgressHistoryDialog } from 'shared/dialogs/goal-progress-history';
import { GoalProgressUpdateDialog } from 'shared/dialogs/goal-progress-update';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { GoalMetric } from 'shared/types/goal-metric';
import { GoalProgressHistoryFormFields } from 'shared/types/goal-progress-form';
import { ID } from 'shared/types/id';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import {
  GoalProgressContext,
  GoalProgressContextProps,
} from './goal-progress-context';

enum DialogOptions {
  History = 'history',
  Update = 'update',
}

export type GoalProgressProviderProps = {
  children: React.ReactNode;
};

export const GoalProgressProvider: React.FC<GoalProgressProviderProps> = ({
  children,
}) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const track = useTrackEvents();

  const [openedDialog, setOpenedDialog] = useState<DialogOptions | undefined>();
  const [metric, setMetric] = React.useState<GoalMetric>();
  const [goalId, setGoalId] = React.useState<ID>();

  const { submit, isLoading, error, retry } = useUpdateGoal();

  const openDialog = useCallback(
    (goalId: ID, metric: GoalMetric, dialog: DialogOptions) => {
      setMetric(metric);
      setGoalId(goalId);
      setOpenedDialog(dialog);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setMetric(undefined);
    setGoalId(undefined);
    setOpenedDialog(undefined);
  }, []);

  const toggleDialogs = useCallback(
    () =>
      setOpenedDialog((oldOpenDialog) =>
        oldOpenDialog === DialogOptions.History
          ? DialogOptions.Update
          : DialogOptions.History,
      ),
    [],
  );

  const submitForm = useCallback(
    (entries: GoalProgressHistoryFormFields['entries']) => {
      if (!goalId || !metric) {
        return;
      }

      const newMetric = {
        ...metric,
        entries,
      };

      submit({
        id: goalId,
        metric: newMetric,
      });

      track(ActionEvents.GoalProgressUpdated, undefined);

      setMetric(newMetric);
    },
    [goalId, metric, submit, track],
  );

  const value = useMemo<GoalProgressContextProps>(
    () => ({
      onOpenGoalProgressHistory: (...args) =>
        openDialog(...args, DialogOptions.History),
      onOpenGoalProgressAddEntry: (...args) =>
        openDialog(...args, DialogOptions.Update),
    }),
    [openDialog],
  );

  return (
    <GoalProgressContext.Provider value={value}>
      {children}

      <GoalProgressHistoryDialog
        open={openedDialog === DialogOptions.History}
        initialValues={{ entries: metric?.entries ?? [] }}
        unit={metric?.unit ?? undefined}
        target={metric?.targetValue ?? undefined}
        startValue={metric?.startValue ?? undefined}
        dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
        weekStartsOn={weekStartsOn}
        onClose={closeDialog}
        onSubmit={submitForm}
        onAdd={toggleDialogs}
        onRetry={retry}
        isLoading={isLoading}
        isError={!!error}
      />

      {openedDialog === DialogOptions.Update && (
        <GoalProgressUpdateDialog
          open
          initialValues={{ entries: metric?.entries ?? [] }}
          unit={metric?.unit ?? undefined}
          startValue={metric?.startValue ?? undefined}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={weekStartsOn}
          onClose={closeDialog}
          onSubmit={submitForm}
          onOverview={toggleDialogs}
          onRetry={retry}
          isLoading={isLoading}
          isError={!!error}
        />
      )}
    </GoalProgressContext.Provider>
  );
};
